import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Cpu, Waves, MapPin, Users, Shield, Target } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import PublicSimulator from "@/pages/public/PublicSimulator";

/*
 * Titan Infra — public home page (post-refactor).
 *
 * Editorial rules for this page (imposed by user):
 *   • No prices, no amounts, no yields, no "buy" or "subscribe" buttons.
 *   • Ownership language avoided — participation is framed as capacity operated by Titan Infra.
 *   • Single discreet CTA at the bottom: "Accès" → /login. No self-serve registration.
 *   • Tone: institutional / infrastructure, not commercial offer.
 */

const CINEMATIC_VIDEO = "https://customer-assets-eiarnc6j.emergentagent.net/job_titan-infra/artifacts/nh2offxg_openart-95e00c38-ea4_d90b4d5f_1785622858182.MP4";
const INFRA_VIDEO = "/media/onsite-batam.mp4";
const HERO_VIDEO = "https://videos.pexels.com/video-files/3141210/3141210-hd_1920_1080_25fps.mp4";
const HERO_POSTER = "https://images.unsplash.com/photo-1639066648921-82d4500abf1a?w=1600&q=80";

const TEAM_PREVIEW = [
  { name: "Doudou David",   role: "CEO — Strategy Intelligence Solutions", img: "/team/david-doudou.png" },
  { name: "Patrick Willems", role: "Business Development Engineer",         img: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=800&q=80" },
  { name: "Ayu Wijaya",      role: "Head of Indonesia — Europe Operations", img: "/team/ayu-wijaya.png" },
  { name: "Wei Lin",         role: "Chief Technology Officer",              img: "/team/wei-lin.png" },
];

const COPY = {
  en: {
    hero: {
      badge: "Community-scale AI infrastructure · Indonesia",
      titleA: "The infrastructure",
      titleB: "behind",
      titleC: "artificial intelligence.",
      lead: "Titan Infra operates modular AI compute capacity in Batam, Indonesia — a purpose-built site engineered for training and serving next-generation AI models.",
      note: "Operated by Strategy Intelligence Solutions LTD · United Kingdom",
    },
    vision: {
      eyebrow: "Vision & mission",
      title: "Infrastructure, not speculation.",
      body: "Artificial intelligence is now a utility layer of the world economy. Every model, every query, every inference draws on physical machines running inside a small number of data centers. Titan Infra exists to build and operate those machines — sustainably, close to the demand curve of Asia — and to open a form of participation in the compute capacity we run.",
      pillars: [
        { icon: Target, title: "Purpose", body: "Bring AI compute closer to where it is consumed, starting with South-East Asia." },
        { icon: Shield, title: "Approach", body: "Modular container-grade data centers, GPU-optimised, deployed in successive waves." },
        { icon: Users,  title: "Access",  body: "A structured way to be associated with the operating capacity we run — no speculation." },
      ],
    },
    infra: {
      eyebrow: "Infrastructure — Batam, Indonesia",
      title: "A next-generation AI campus in South-East Asia.",
      body: "Nongsa Digital Park, on Batam island, sits inside a Special Economic Zone one hour by sea from Singapore. Direct subsea fibre, tropical grid backed by industrial solar, and modular container architecture make it one of the most efficient sites available for GPU-dense workloads in the region.",
      specs: [
        { icon: MapPin, k: "Site",       v: "Nongsa Digital Park · Batam, Indonesia" },
        { icon: Zap,    k: "Capacity",   v: "50 MW phase I — 200 MW target" },
        { icon: Cpu,    k: "GPU stack",  v: "NVIDIA H100 · B200 modular clusters" },
        { icon: Waves,  k: "Energy mix", v: "Grid + industrial solar · target 30% renewable" },
      ],
      timeline: [
        { stage: "Site preparation",   status: "In progress" },
        { stage: "Modular deployment", status: "In progress" },
        { stage: "First Titan Units delivered", status: "August 2026" },
        { stage: "First compute revenue", status: "Q4 2026" },
      ],
    },
    unitsBlock: {
      eyebrow: "The Titan Unit",
      title: "A new way to be associated with the machines that power AI.",
      lead: "A Titan Unit represents one indivisible unit of AI compute capacity operated by Titan Infra. Holding Titan Units means being formally associated with a fraction of the machinery that trains and serves modern AI — not a share, not a token, not a promise. A unit of real, running infrastructure.",
      points: [
        "Each Titan Unit corresponds to a defined slice of GPU-hours our Batam site is engineered to produce over its lifetime.",
        "Units are issued in cohorts as the modular capacity is commissioned. They are not tradable on any public market.",
        "Titan Infra remains the sole operator of the underlying infrastructure. Unit holders are informed of operational milestones through a private space.",
      ],
      disclaimer: "The Titan Unit is a structured participation instrument. It is not a share, not a security offered to the public, and not a financial promise. Detailed terms are shared privately with prospective holders.",
    },
    team: {
      eyebrow: "Team & credibility",
      title: "Operators, not marketers.",
      lead: "Titan Infra is operated by a small, senior team combining finance, infrastructure engineering and on-the-ground presence in Batam.",
      cta: "See the full team",
    },
    access: {
      eyebrow: "Private access",
      title: "Existing holders and invited prospects.",
      lead: "Access to your space, your documents and your unit statement.",
      cta: "Accès",
    },
  },
  fr: {
    hero: {
      badge: "Infrastructure IA communautaire · Indonésie",
      titleA: "L'infrastructure",
      titleB: "derrière",
      titleC: "l'intelligence artificielle.",
      lead: "Titan Infra opère une capacité de calcul IA modulaire à Batam, en Indonésie — un site pensé pour entraîner et servir les modèles d'intelligence artificielle de nouvelle génération.",
      note: "Opéré par Strategy Intelligence Solutions LTD · Royaume-Uni",
    },
    vision: {
      eyebrow: "Vision & mission",
      title: "De l'infrastructure, pas de la spéculation.",
      body: "L'intelligence artificielle est devenue une couche utilitaire de l'économie mondiale. Chaque modèle, chaque requête, chaque inférence repose sur des machines physiques qui tournent dans un petit nombre de data centers. Titan Infra existe pour construire et opérer ces machines — durablement, au plus proche de la demande asiatique — et pour ouvrir une forme de participation à la capacité de calcul que nous opérons.",
      pillars: [
        { icon: Target, title: "Vocation",  body: "Rapprocher la capacité IA de son lieu de consommation, en commençant par l'Asie du Sud-Est." },
        { icon: Shield, title: "Approche",  body: "Des data centers modulaires en containers, optimisés GPU, déployés par vagues successives." },
        { icon: Users,  title: "Accès",     body: "Une façon structurée d'être associé à la capacité opérée — sans spéculation." },
      ],
    },
    infra: {
      eyebrow: "Infrastructure — Batam, Indonésie",
      title: "Un campus IA de nouvelle génération en Asie du Sud-Est.",
      body: "Nongsa Digital Park, sur l'île de Batam, se trouve dans une Zone Économique Spéciale à une heure de bateau de Singapour. Fibre sous-marine directe, réseau électrique tropical adossé à du solaire industriel, et architecture modulaire en containers en font l'un des sites les plus efficaces de la région pour des charges GPU denses.",
      specs: [
        { icon: MapPin, k: "Site",         v: "Nongsa Digital Park · Batam, Indonésie" },
        { icon: Zap,    k: "Capacité",     v: "50 MW phase I — objectif 200 MW" },
        { icon: Cpu,    k: "Parc GPU",     v: "Clusters modulaires NVIDIA H100 · B200" },
        { icon: Waves,  k: "Mix énergie",  v: "Réseau + solaire industriel · cible 30 % renouvelable" },
      ],
      timeline: [
        { stage: "Préparation du site",           status: "En cours" },
        { stage: "Déploiement modulaire",         status: "En cours" },
        { stage: "Livraison des premières Titan Units", status: "Août 2026" },
        { stage: "Premiers revenus de calcul",    status: "Q4 2026" },
      ],
    },
    unitsBlock: {
      eyebrow: "La Titan Unit",
      title: "Une nouvelle façon d'être associé aux machines qui alimentent l'IA.",
      lead: "Une Titan Unit représente une unité indivisible de capacité de calcul IA exploitée par Titan Infra. Détenir des Titan Units, c'est être formellement associé à une fraction de l'infrastructure qui entraîne et sert l'IA moderne — ce n'est pas une action, ni un jeton, ni une promesse. C'est une unité d'infrastructure réelle, en fonctionnement.",
      points: [
        "Chaque Titan Unit correspond à une part définie d'heures GPU que notre site de Batam est conçu pour produire sur sa durée d'exploitation.",
        "Les Units sont émises par cohortes au fur et à mesure de la mise en service de la capacité modulaire. Elles ne sont pas négociables sur un marché public.",
        "Titan Infra reste le seul opérateur de l'infrastructure sous-jacente. Les détenteurs sont informés des étapes opérationnelles via un espace privé.",
      ],
      disclaimer: "La Titan Unit est un instrument de participation structuré. Ce n'est pas une action, pas une valeur mobilière offerte au public, et ne constitue pas une promesse financière. Les termes détaillés sont partagés en privé avec les personnes concernées.",
    },
    team: {
      eyebrow: "Équipe & crédibilité",
      title: "Des opérateurs, pas des vendeurs.",
      lead: "Titan Infra est opéré par une équipe restreinte et senior, alliant finance, ingénierie d'infrastructure et présence terrain à Batam.",
      cta: "Voir toute l'équipe",
    },
    access: {
      eyebrow: "Accès privé",
      title: "Détenteurs actuels et prospects invités.",
      lead: "Accès à votre espace, vos documents et le relevé de vos Titan Units.",
      cta: "Accès",
    },
  },
  es: {
    hero: {
      badge: "Infraestructura IA comunitaria · Indonesia",
      titleA: "La infraestructura",
      titleB: "detrás de",
      titleC: "la inteligencia artificial.",
      lead: "Titan Infra opera capacidad de cálculo IA modular en Batam, Indonesia — un sitio construido para entrenar y servir modelos de IA de nueva generación.",
      note: "Operado por Strategy Intelligence Solutions LTD · Reino Unido",
    },
    vision: {
      eyebrow: "Visión y misión",
      title: "Infraestructura, no especulación.",
      body: "La inteligencia artificial se ha convertido en una capa utilitaria de la economía mundial. Cada modelo, cada consulta, cada inferencia se apoya en máquinas físicas que funcionan dentro de un pequeño número de data centers. Titan Infra existe para construir y operar esas máquinas — de forma sostenible, cerca de la demanda asiática — y para abrir una forma de participación en la capacidad que operamos.",
      pillars: [
        { icon: Target, title: "Vocación", body: "Acercar la capacidad IA a su lugar de consumo, empezando por el sudeste asiático." },
        { icon: Shield, title: "Enfoque",  body: "Data centers modulares en contenedores, optimizados GPU, desplegados por oleadas." },
        { icon: Users,  title: "Acceso",   body: "Una forma estructurada de asociarse a la capacidad operada — sin especulación." },
      ],
    },
    infra: {
      eyebrow: "Infraestructura — Batam, Indonesia",
      title: "Un campus IA de nueva generación en el sudeste asiático.",
      body: "Nongsa Digital Park, en la isla de Batam, está dentro de una Zona Económica Especial a una hora en barco de Singapur. Fibra submarina directa, red eléctrica tropical respaldada por solar industrial y arquitectura modular en contenedores lo hacen uno de los sitios más eficientes de la región para cargas GPU densas.",
      specs: [
        { icon: MapPin, k: "Sitio",       v: "Nongsa Digital Park · Batam, Indonesia" },
        { icon: Zap,    k: "Capacidad",   v: "50 MW fase I — objetivo 200 MW" },
        { icon: Cpu,    k: "Parque GPU",  v: "Clústeres modulares NVIDIA H100 · B200" },
        { icon: Waves,  k: "Mix energía", v: "Red + solar industrial · meta 30 % renovable" },
      ],
      timeline: [
        { stage: "Preparación del sitio",             status: "En curso" },
        { stage: "Despliegue modular",                status: "En curso" },
        { stage: "Entrega de las primeras Titan Units", status: "Agosto 2026" },
        { stage: "Primeros ingresos de cálculo",      status: "Q4 2026" },
      ],
    },
    unitsBlock: {
      eyebrow: "La Titan Unit",
      title: "Una nueva forma de asociarse a las máquinas que impulsan la IA.",
      lead: "Una Titan Unit representa una unidad indivisible de capacidad de cálculo IA operada por Titan Infra. Detentar Titan Units significa estar formalmente asociado a una fracción de la maquinaria que entrena y sirve la IA moderna — no es una acción, ni un token, ni una promesa. Es una unidad de infraestructura real, en funcionamiento.",
      points: [
        "Cada Titan Unit corresponde a una parte definida de horas GPU que nuestro sitio de Batam está diseñado para producir durante su vida útil.",
        "Las Units se emiten por cohortes a medida que la capacidad modular se pone en servicio. No son negociables en un mercado público.",
        "Titan Infra sigue siendo el único operador de la infraestructura subyacente. Los detentores son informados de los hitos operativos vía un espacio privado.",
      ],
      disclaimer: "La Titan Unit es un instrumento de participación estructurado. No es una acción, ni un valor ofrecido al público, y no constituye una promesa financiera. Los términos detallados se comparten en privado.",
    },
    team: {
      eyebrow: "Equipo y credibilidad",
      title: "Operadores, no vendedores.",
      lead: "Titan Infra es operado por un equipo reducido y sénior, que combina finanzas, ingeniería de infraestructura y presencia sobre el terreno en Batam.",
      cta: "Ver todo el equipo",
    },
    access: {
      eyebrow: "Acceso privado",
      title: "Detentores actuales y prospectos invitados.",
      lead: "Acceso a tu espacio, tus documentos y el estado de tus Titan Units.",
      cta: "Acceso",
    },
  },
};

export default function Home() {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;

  return (
    <>
      {/* ─── HERO ─── */}
      <section className="relative overflow-hidden bg-black text-white">
        <div className="absolute inset-0 z-0">
          <video
            data-testid="hero-video"
            autoPlay muted loop playsInline preload="metadata"
            poster={HERO_POSTER}
            className="w-full h-full object-cover opacity-70"
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_80%,rgba(16,185,129,0.12),transparent_50%),radial-gradient(ellipse_at_80%_20%,rgba(56,189,248,0.10),transparent_50%)]" />
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-24 md:pb-32 min-h-[80vh] md:min-h-[92vh] flex flex-col justify-end">
          <motion.span
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            data-testid="hero-badge"
            className="inline-flex self-start items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 backdrop-blur text-[11px] tracking-widest uppercase text-white/80"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            {c.hero.badge}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.15, ease: [0.2, 0.7, 0.2, 1] }}
            className="mt-10 font-display font-light tracking-tighter text-[44px] sm:text-[68px] md:text-[92px] lg:text-[108px] leading-[0.94] max-w-6xl"
            data-testid="hero-title"
          >
            <span className="block">{c.hero.titleA}</span>
            <span className="block">{c.hero.titleB}</span>
            <span className="block bg-gradient-to-r from-emerald-400 to-sky-400 bg-clip-text text-transparent italic font-light">
              {c.hero.titleC}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-10 max-w-2xl text-[17px] md:text-[20px] leading-relaxed text-white/70"
          >
            {c.hero.lead}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.9 }}
            className="mt-10 text-[11px] tracking-widest uppercase text-white/40"
          >
            {c.hero.note}
          </motion.p>
        </div>
      </section>

      {/* ─── VISION ─── */}
      <section data-testid="section-vision" className="bg-black text-white border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16">
            <div className="lg:col-span-5">
              <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-medium text-emerald-400">
                <span className="w-6 h-px bg-emerald-400" /> {c.vision.eyebrow}
              </p>
              <h2 className="mt-4 font-display font-light tracking-tighter text-4xl md:text-6xl leading-[1.02]">{c.vision.title}</h2>
            </div>
            <div className="lg:col-span-7 lg:pt-4">
              <p className="text-[16px] md:text-[18px] leading-relaxed text-white/70 max-w-2xl">{c.vision.body}</p>
              <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
                {c.vision.pillars.map((p, i) => {
                  const I = p.icon;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.6, delay: i * 0.08 }}
                      data-testid={`vision-pillar-${i}`}
                      className="rounded-2xl border border-white/10 bg-white/[0.02] p-6"
                    >
                      <I className="w-5 h-5 text-emerald-400" />
                      <p className="mt-5 font-display text-xl tracking-tight">{p.title}</p>
                      <p className="mt-2 text-[13px] leading-relaxed text-white/60">{p.body}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── INFRASTRUCTURE (Indonesia) ─── */}
      <section data-testid="section-infrastructure" className="bg-zinc-950 text-white border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="max-w-3xl">
            <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-medium text-sky-400">
              <span className="w-6 h-px bg-sky-400" /> {c.infra.eyebrow}
            </p>
            <h2 className="mt-4 font-display font-light tracking-tighter text-4xl md:text-6xl leading-[1.02]">{c.infra.title}</h2>
            <p className="mt-6 text-[15px] leading-relaxed text-white/60 max-w-2xl">{c.infra.body}</p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {c.infra.specs.map((s, i) => {
              const I = s.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  data-testid={`infra-spec-${i}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-7"
                >
                  <I className="w-5 h-5 text-sky-400" />
                  <p className="mt-6 text-[11px] tracking-widest uppercase text-white/50">{s.k}</p>
                  <p className="mt-2 font-display font-light text-[19px] leading-tight tracking-tight">{s.v}</p>
                </motion.div>
              );
            })}
          </div>

          {/* On-site footage — Batam buildout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
            className="mt-14 relative overflow-hidden rounded-2xl md:rounded-[24px] border border-white/10 bg-black shadow-[0_30px_80px_-30px_rgba(56,189,248,0.30)]"
            data-testid="infra-video-section"
          >
            <video
              data-testid="infra-video"
              src={INFRA_VIDEO}
              poster="/media/onsite-batam-poster.jpg"
              autoPlay muted loop playsInline preload="auto"
              className="block w-full h-auto aspect-video object-cover"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
            <div className="absolute bottom-0 left-0 right-0 p-5 md:p-8 flex items-end justify-between gap-4 flex-wrap">
              <div>
                <p className="text-[10px] tracking-[0.22em] uppercase text-white/50">On-site footage · Batam</p>
                <p className="mt-2 font-display font-light text-xl md:text-2xl tracking-tight text-white">Our infrastructure, in real conditions.</p>
              </div>
              <span className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[10px] tracking-widest uppercase text-white/80">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Buildout in progress
              </span>
            </div>
          </motion.div>


          {/* Timeline */}
          <div className="mt-16 rounded-2xl border border-white/10 bg-black/40 p-6 md:p-10">
            <p className="text-[11px] tracking-widest uppercase text-white/50">Timeline</p>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-5">
              {c.infra.timeline.map((t, i) => (
                <div key={i} data-testid={`infra-timeline-${i}`} className="border-l border-white/10 pl-4">
                  <p className="font-display text-[15px] tracking-tight leading-snug">{t.stage}</p>
                  <p className="mt-2 text-[11px] tracking-widest uppercase text-emerald-400">{t.status}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── CINEMATIC VIDEO SHOWCASE ─── */}
      <section data-testid="cinematic-video-section" className="relative bg-black text-white border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-6 md:pb-10">
          <div className="max-w-3xl">
            <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-medium text-white/60">
              <span className="w-6 h-px bg-white/40" /> Inside the machine
            </p>
            <h2 className="mt-4 font-display font-light tracking-tighter text-3xl md:text-5xl leading-[1.02]">The machines behind intelligence.</h2>
          </div>
        </div>
        <div className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.1, ease: [0.2, 0.7, 0.2, 1] }}
            className="relative mx-auto max-w-[1600px] px-4 md:px-8 pb-24 md:pb-32"
          >
            <div className="relative overflow-hidden rounded-2xl md:rounded-[28px] border border-white/10 bg-black shadow-[0_40px_120px_-20px_rgba(16,185,129,0.25)]">
              <video
                data-testid="cinematic-video"
                src={CINEMATIC_VIDEO}
                autoPlay muted loop playsInline preload="auto"
                className="block w-full h-auto aspect-video object-cover"
              />
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_55%,rgba(0,0,0,0.55)_100%)]" />
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_20%_90%,rgba(16,185,129,0.10),transparent_60%),radial-gradient(ellipse_at_80%_10%,rgba(56,189,248,0.10),transparent_60%)]" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── TITAN UNITS (narrative — no prices, no CTA) ─── */}
      <section data-testid="section-units" className="bg-white text-zinc-900 border-t border-black/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16">
            <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
              <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-medium text-zinc-500">
                <span className="w-6 h-px bg-current" /> {c.unitsBlock.eyebrow}
              </p>
              <h2 className="mt-4 font-display font-light tracking-tighter text-4xl md:text-6xl leading-[1.02]">{c.unitsBlock.title}</h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-[17px] md:text-[19px] leading-relaxed text-zinc-700">{c.unitsBlock.lead}</p>
              <ul className="mt-10 space-y-6">
                {c.unitsBlock.points.map((p, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, delay: i * 0.08 }}
                    data-testid={`units-point-${i}`}
                    className="flex items-start gap-4"
                  >
                    <span className="mt-2 shrink-0 w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-[15px] leading-relaxed text-zinc-700">{p}</span>
                  </motion.li>
                ))}
              </ul>
              <p className="mt-14 text-[11px] leading-relaxed text-zinc-500 max-w-2xl">{c.unitsBlock.disclaimer}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PUBLIC COMPUTE SIMULATOR ─── */}
      <PublicSimulator />

      {/* ─── TEAM (preview → link to full page) ─── */}
      <section data-testid="section-team" className="bg-zinc-50 border-t border-black/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="max-w-3xl">
            <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-medium text-zinc-500">
              <span className="w-6 h-px bg-current" /> {c.team.eyebrow}
            </p>
            <h2 className="mt-4 font-display font-light tracking-tighter text-4xl md:text-6xl leading-[1.02] text-zinc-900">{c.team.title}</h2>
            <p className="mt-6 text-[16px] leading-relaxed text-zinc-600 max-w-2xl">{c.team.lead}</p>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {TEAM_PREVIEW.map((m, i) => (
              <Link
                key={m.name}
                to="/team"
                data-testid={`home-team-member-${i}`}
                className="group block rounded-2xl overflow-hidden border border-black/5 bg-white transition-shadow hover:shadow-lg"
              >
                <div className="aspect-[4/5] overflow-hidden bg-zinc-200 relative">
                  <img
                    src={m.img}
                    alt={m.name}
                    className="w-full h-full object-cover object-[center_20%] grayscale group-hover:grayscale-0 transition-all duration-1000 ease-out"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="font-display text-[15px] tracking-tight text-zinc-900">{m.name}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-widest text-emerald-600 font-medium leading-relaxed">{m.role}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-10">
            <Link
              to="/team"
              data-testid="home-team-cta"
              className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase font-medium text-zinc-900 border-b border-zinc-900/30 pb-1 hover:border-zinc-900 transition-colors"
            >
              {c.team.cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── DISCREET ACCESS CTA (footer of page) ─── */}
      <section data-testid="section-access" className="bg-black text-white border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-28 flex flex-col items-start md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-xl">
            <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-medium text-white/50">
              <span className="w-6 h-px bg-white/40" /> {c.access.eyebrow}
            </p>
            <h2 className="mt-4 font-display font-light tracking-tighter text-3xl md:text-4xl leading-[1.05]">{c.access.title}</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/60 max-w-md">{c.access.lead}</p>
          </div>
          <Link
            to="/login"
            data-testid="access-cta"
            className="inline-flex items-center gap-2 h-11 md:h-12 px-6 md:px-7 rounded-full border border-white/25 hover:bg-white/10 text-[12px] tracking-widest uppercase text-white transition-colors"
          >
            {c.access.cta} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>
    </>
  );
}
